import React, { useMemo, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';

import { exportProgressDocumentToPdf } from '../../PlanBuilder/services/PlanDocumentPdfService';
import { generatePlanDocumentByType } from '../../PlanBuilder/services/DocumentGeneratorService';

const ADMIN_PROGRESS_DETAIL_QUERY = gql`
  query AdminPlanBuilderProgressDetail($id: ID!) {
    currentUserIsAdmin
    adminUserPlanProgress(id: $id) {
      id
      status
      completionPercentage
      ceStatus
      startedAt
      pausedAt
      scheduledResumeAt
      lastActivityAt
      resumeReminderStatus
      resumeReminderScheduledAt
      resumeReminderLastError
      user {
        id
        name
        email
      }
      planTemplate {
        id
        name
        slug
        planType
      }
      userPlanResponses {
        id
        completedAt
        answer
        planQuestion {
          id
          questionText
        }
      }
    }
  }
`;

const ADMIN_PLAN_BUILDER_PROGRESS_EXPORT_QUERY = gql`
  query AdminPlanBuilderProgressExport($id: ID!) {
    adminUserPlanProgress(id: $id) {
      id
      status
      completionPercentage
      planTemplate {
        id
        name
        slug
        planType
        description
        planModules {
          id
          title
          planSections {
            id
            title
            planQuestions {
              id
              questionText
              widgetType
              options
              purpose
              category
              outputSection
              gapAction
              metadata
            }
          }
        }
      }
      user {
        id
        name
        allowed_name
        email
        company {
          name
        }
      }
      userPlanResponses {
        id
        answer
        planQuestionId
      }
    }
  }
`;

const ADMIN_RESUME_PROGRESS = gql`
  mutation AdminResumeUserPlanProgress($id: ID!) {
    admin_resume_user_plan_progress(id: $id) {
      user_plan_progress {
        id
        status
        pausedAt
        scheduledResumeAt
        resumeReminderStatus
      }
      errors
    }
  }
`;

const ADMIN_CLEAR_SCHEDULE = gql`
  mutation AdminClearScheduledResume($id: ID!) {
    admin_clear_scheduled_resume(id: $id) {
      user_plan_progress {
        id
        scheduledResumeAt
        resumeReminderStatus
      }
      errors
    }
  }
`;

const ADMIN_RESET_PROGRESS = gql`
  mutation AdminResetUserPlanProgress($userId: ID!, $planTemplateId: ID!) {
    admin_reset_user_plan_progress(userId: $userId, planTemplateId: $planTemplateId) {
      success
      errors
    }
  }
`;

const ADMIN_FAST_FORWARD_PROGRESS = gql`
  mutation AdminFastForwardUserPlanProgress($id: ID!) {
    admin_fast_forward_user_plan_progress(id: $id) {
      success
      errors
    }
  }
`;

const fmt = (dt) => (dt ? new Date(dt).toLocaleString() : '—');

const toTitle = (s) => {
  const raw = String(s || '').trim();
  if (!raw) return '—';
  return raw
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const CHIP_STYLE = {
  height: 22,
  fontSize: 11,
  fontWeight: 600,
};

const CHIP_STYLE_INFO = {
  ...CHIP_STYLE,
  backgroundColor: '#E3F2FD',
  color: '#0D47A1',
  border: '1px solid #90CAF9',
};

const CHIP_STYLE_SUCCESS = {
  ...CHIP_STYLE,
  backgroundColor: '#2E7D32',
  color: '#FFFFFF',
};

const CHIP_STYLE_MUTED = {
  ...CHIP_STYLE,
  backgroundColor: '#F3F4F6',
  color: '#374151',
  border: '1px solid #D1D5DB',
};

const withChipStyle = (props, style) => ({ ...props, style: { ...(props.style || {}), ...(style || {}) } });

const statusChip = (status) => {
  const s = String(status || '');
  if (s === 'completed') return withChipStyle({ label: 'Completed', variant: 'default' }, CHIP_STYLE_SUCCESS);
  if (s === 'in_progress') return withChipStyle({ label: 'In Progress', variant: 'default' }, CHIP_STYLE_INFO);
  if (s === 'paused') return withChipStyle({ label: 'Paused', variant: 'outlined' }, CHIP_STYLE);
  if (s === 'not_started') return withChipStyle({ label: 'Not Started', variant: 'outlined' }, CHIP_STYLE_MUTED);
  return withChipStyle({ label: toTitle(s), variant: 'outlined' }, CHIP_STYLE);
};

const reminderChip = (status) => {
  const s = String(status || '');
  if (!s) return withChipStyle({ label: '—', variant: 'outlined' }, CHIP_STYLE_MUTED);
  if (s === 'scheduled') return withChipStyle({ label: 'Scheduled', variant: 'default' }, CHIP_STYLE_SUCCESS);
  if (s === 'pending') return withChipStyle({ label: 'Pending', variant: 'default' }, CHIP_STYLE_INFO);
  if (s === 'failed') return withChipStyle({ label: 'Failed', variant: 'outlined' }, CHIP_STYLE);
  if (s === 'disabled') return withChipStyle({ label: 'Disabled', variant: 'outlined' }, CHIP_STYLE_MUTED);
  return withChipStyle({ label: toTitle(s), variant: 'outlined' }, CHIP_STYLE);
};

const ceChip = (status) => {
  const s = String(status || '');
  if (!s || s === '—') return withChipStyle({ label: '—', variant: 'outlined' }, CHIP_STYLE_MUTED);
  if (s.toLowerCase() === 'passed') return withChipStyle({ label: 'Passed', variant: 'default' }, CHIP_STYLE_SUCCESS);
  if (s.toLowerCase() === 'failed') return withChipStyle({ label: 'Failed', variant: 'outlined' }, CHIP_STYLE);
  if (s.toLowerCase() === 'not started') return withChipStyle({ label: 'Not Started', variant: 'outlined' }, CHIP_STYLE_MUTED);
  if (s.toLowerCase() === 'n/a') return withChipStyle({ label: 'N/A', variant: 'outlined' }, CHIP_STYLE_MUTED);
  return withChipStyle({ label: toTitle(s), variant: 'outlined' }, CHIP_STYLE);
};

const formatAnswerForHumans = (answer) => {
  if (!answer) return '—';

  const formatted = answer.formatted;
  if (typeof formatted === 'string' && formatted.trim().length) return formatted;

  const value = answer.value;
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.join(', ');

  // Object/unknown type fallback (still readable, but not raw JSON)
  try {
    return JSON.stringify(value, null, 2);
  } catch (_e) {
    return String(value);
  }
};

const PlanBuilderProgressDetail = (props) => {
  const id = props?.match?.params?.id;
  const [responseTabById, setResponseTabById] = useState({});
  const [downloading, setDownloading] = useState(false);
  const [fastForwardOpen, setFastForwardOpen] = useState(false);
  const apolloClient = useApolloClient();

  const { data, loading, error, refetch } = useQuery(ADMIN_PROGRESS_DETAIL_QUERY, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const isAdmin = Boolean(data?.currentUserIsAdmin);
  const progress = data?.adminUserPlanProgress;

  const [resumeProgress, resumeState] = useMutation(ADMIN_RESUME_PROGRESS);
  const [clearSchedule, clearState] = useMutation(ADMIN_CLEAR_SCHEDULE);
  const [resetProgress, resetState] = useMutation(ADMIN_RESET_PROGRESS);
  const [fastForwardProgress, fastForwardState] = useMutation(ADMIN_FAST_FORWARD_PROGRESS);

  const isMutating = resumeState.loading || clearState.loading || resetState.loading || fastForwardState.loading;

  const sortedResponses = useMemo(() => {
    const r = progress?.userPlanResponses || [];
    return [...r].sort((a, b) => (a.completedAt || '').localeCompare(b.completedAt || ''));
  }, [progress?.userPlanResponses]);

  return (
    <Box p={3}>
      <Box mb={2}>
        <Typography variant="h4" gutterBottom>Plan Builder Progress Detail</Typography>
        <Typography variant="body2" color="textSecondary">
          <Link to="/admin/plan_builder/progress">Back to progress index</Link>
        </Typography>
      </Box>

      {error && <Typography color="error">Error: {error.message}</Typography>}
      {loading && <Typography color="textSecondary">Loading...</Typography>}

      {progress && (
        <>
          {/*
            Fast Forward is intentionally guarded both client-side and server-side.
            Client: only show to role=admin, incomplete plans, and Succession template slug.
            Server: re-check authorization + eligibility before seeding.
          */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Summary</Typography>
                  <Divider style={{ margin: '12px 0' }} />
                  <Typography><strong>User:</strong> {progress.user?.name || '—'} ({progress.user?.email || '—'})</Typography>
                  <Typography><strong>Template:</strong> {progress.planTemplate?.name || progress.planTemplate?.slug || '—'}</Typography>
                  <Typography>
                    <strong>Status:</strong>{' '}
                    <Chip size="small" {...statusChip(progress.status)} />
                  </Typography>
                  <Typography>
                    <strong>CE:</strong>{' '}
                    <Chip size="small" {...ceChip(progress.ceStatus || '—')} />
                  </Typography>
                  <Typography><strong>Completion:</strong> {progress.completionPercentage}%</Typography>
                  <Typography><strong>Started:</strong> {fmt(progress.startedAt)}</Typography>
                  <Typography><strong>Paused:</strong> {fmt(progress.pausedAt)}</Typography>
                  <Typography><strong>Scheduled resume:</strong> {fmt(progress.scheduledResumeAt)}</Typography>
                  <Typography><strong>Last activity:</strong> {fmt(progress.lastActivityAt)}</Typography>
                  <Box mt={2}>
                    <Typography variant="subtitle2">Reminder</Typography>
                    <Typography>
                      <strong>Status:</strong>{' '}
                      <Chip size="small" {...reminderChip(progress.resumeReminderStatus)} />
                    </Typography>
                    <Typography><strong>Scheduled at:</strong> {fmt(progress.resumeReminderScheduledAt)}</Typography>
                    {progress.resumeReminderLastError && (
                      <Typography color="error"><strong>Error:</strong> {progress.resumeReminderLastError}</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Admin actions</Typography>
                  <Divider style={{ margin: '12px 0' }} />
                  <Box display="flex" flexDirection="column" gridGap={8}>
                    {isAdmin && progress.status !== 'completed' && progress.planTemplate?.slug === 'comprehensive-succession-plan-v2' && (
                      <Button
                        variant="contained"
                        color="default"
                        disabled={isMutating}
                        onClick={async () => {
                          setFastForwardOpen(true);
                        }}
                      >
                        Fast Forward (DEMO)
                      </Button>
                    )}

                    <Dialog
                      open={fastForwardOpen}
                      onClose={() => setFastForwardOpen(false)}
                      aria-labelledby="fast-forward-demo-dialog-title"
                    >
                      <DialogTitle id="fast-forward-demo-dialog-title">Fast Forward (DEMO)</DialogTitle>
                      <DialogContent>
                        <Typography>
                          Clicking this button will erase all responses and should only be used for demo purposes
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setFastForwardOpen(false)} disabled={isMutating}>
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="secondary"
                          disabled={isMutating || !progress}
                          onClick={async () => {
                            try {
                              const res = await fastForwardProgress({ variables: { id: progress.id } });
                              const payload = res?.data?.admin_fast_forward_user_plan_progress;
                              if (!payload?.success) {
                                // eslint-disable-next-line no-alert
                                window.alert(`Fast Forward failed: ${(payload?.errors || ['Unknown error']).join(', ')}`);
                                return;
                              }
                              setFastForwardOpen(false);
                              await refetch();
                            } catch (e) {
                              // eslint-disable-next-line no-alert
                              window.alert(`Fast Forward failed: ${e?.message || e}`);
                            }
                          }}
                        >
                          Fast Forward (DEMO)
                        </Button>
                      </DialogActions>
                    </Dialog>

                    <Button
                      variant="contained"
                      disabled={isMutating || downloading || progress.status !== 'completed'}
                      onClick={async () => {
                        setDownloading(true);
                        try {
                          const { data: exportData } = await apolloClient.query({
                            query: ADMIN_PLAN_BUILDER_PROGRESS_EXPORT_QUERY,
                            variables: { id: progress.id },
                            fetchPolicy: 'network-only',
                          });

                          const fullProgress = exportData?.adminUserPlanProgress;
                          if (!fullProgress) throw new Error('Missing progress payload');

                          const planName = fullProgress?.planTemplate?.name || 'Succession Plan';
                          const userName = fullProgress?.user?.allowed_name || fullProgress?.user?.name || 'User';
                          const companyName = fullProgress?.user?.company?.name || 'Practice';
                          const filename = `${userName.replace(/\\s/g, '_')}_${planName.replace(/\\s/g, '_')}.pdf`;

                          await exportProgressDocumentToPdf({
                            content: generatePlanDocumentByType(fullProgress, userName, companyName),
                            userName,
                            companyName,
                            filename,
                          });
                        } catch (e) {
                          // eslint-disable-next-line no-console
                          console.error('Admin detail export failed:', e);
                          // eslint-disable-next-line no-alert
                          window.alert(`Failed to export PDF: ${e.message || e}`);
                        } finally {
                          setDownloading(false);
                        }
                      }}
                    >
                      {downloading ? 'Downloading…' : 'Download PDF'}
                    </Button>

                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isMutating}
                      onClick={async () => {
                        await resumeProgress({ variables: { id: progress.id } });
                        await refetch();
                      }}
                    >
                      Force Resume
                    </Button>

                    <Button
                      variant="outlined"
                      disabled={isMutating}
                      onClick={async () => {
                        await clearSchedule({ variables: { id: progress.id } });
                        await refetch();
                      }}
                    >
                      Clear Scheduled Resume
                    </Button>

                    <Button
                      variant="outlined"
                      color="secondary"
                      disabled={isMutating}
                      onClick={async () => {
                        // eslint-disable-next-line no-alert
                        const ok = window.confirm('Reset this plan for this user? This will delete their progress and answers.');
                        if (!ok) return;
                        const res = await resetProgress({
                          variables: { userId: progress.user?.id, planTemplateId: progress.planTemplate?.id }
                        });
                        const payload = res?.data?.admin_reset_user_plan_progress;
                        if (payload?.success) {
                          window.location.href = '/admin/plan_builder/progress';
                          return;
                        }
                        // eslint-disable-next-line no-alert
                        window.alert(`Reset failed: ${(payload?.errors || ['Unknown error']).join(', ')}`);
                      }}
                    >
                      Reset Plan For User
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box mt={3}>
            <Typography variant="h6" gutterBottom>Responses ({sortedResponses.length})</Typography>
            <Paper style={{ padding: 16 }}>
              {!sortedResponses.length && (
                <Typography color="textSecondary">No responses yet.</Typography>
              )}
              {sortedResponses.map((r) => (
                <Box key={r.id} mb={2}>
                  <Typography variant="subtitle2">{r.planQuestion?.questionText || 'Question'}</Typography>
                  <Typography variant="body2" color="textSecondary">{fmt(r.completedAt)}</Typography>
                  <Box mt={1}>
                    <Tabs
                      value={responseTabById[r.id] ?? 0}
                      onChange={(_e, newValue) => setResponseTabById((prev) => ({ ...prev, [r.id]: newValue }))}
                      indicatorColor="primary"
                      textColor="primary"
                      style={{ minHeight: 28 }}
                    >
                      <Tab label="Answer" style={{ minHeight: 28, minWidth: 72, padding: '4px 8px', fontSize: 12 }} />
                      <Tab label="JSON" style={{ minHeight: 28, minWidth: 72, padding: '4px 8px', fontSize: 12 }} />
                    </Tabs>
                    <Box mt={1}>
                      {(responseTabById[r.id] ?? 0) === 0 ? (
                        <Typography style={{ whiteSpace: 'pre-wrap' }}>
                          {formatAnswerForHumans(r.answer)}
                        </Typography>
                      ) : (
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                          {JSON.stringify(r.answer, null, 2)}
                        </pre>
                      )}
                    </Box>
                  </Box>
                  <Divider style={{ marginTop: 12 }} />
                </Box>
              ))}
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PlanBuilderProgressDetail;


